import { apiClient } from "../../../shared";
import { normalizeUser } from "../types";

// Profile reads and writes.

// Attaches a display-ready message to the rejected axios error while preserving
// the `isAuthError` tag the client interceptor added. Throwing a fresh Error
// here instead would drop that tag, and callers need it to tell "your session
// expired" apart from "the server is down".
const withUserMessage = (error, fallback) => {
  const data = error?.response?.data;
  error.userMessage =
    (data && typeof data === "object" && (data.message || data.detail)) ||
    fallback;
  return error;
};

/**
 * Read the signed-in user behind the session cookie.
 *
 * Not wired up yet: the session store in src/contexts/AuthContext.jsx still owns
 * the bootstrap read. It lives here so this block declares its whole API surface
 * in one place, and so migrating that store later is a one-line change.
 *
 * @returns {Promise<import("../types/user.types").User|null>}
 */
export const fetchCurrentUser = async () => {
  const { data } = await apiClient.get("user");
  return normalizeUser(data);
};

/**
 * Rename the account.
 * @param {string} username already trimmed and validated by the caller
 */
export const updateUsername = async (username) => {
  try {
    await apiClient.patch(
      `user/username?username=${encodeURIComponent(username)}`,
      null,
      { headers: { "Content-Type": "application/json" } },
    );
    return { username };
  } catch (error) {
    throw withUserMessage(error, "Failed to update.");
  }
};

/**
 * Change the account email.
 * @param {string} email already trimmed and validated by the caller
 */
export const updateEmail = async (email) => {
  try {
    await apiClient.patch(`user/email?email=${encodeURIComponent(email)}`, null, {
      headers: { "Content-Type": "application/json" },
    });
    return { email };
  } catch (error) {
    throw withUserMessage(error, "Failed to update.");
  }
};
